import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scale, FileText, AlertCircle, CheckCircle, Users, Shield, ArrowLeft } from 'lucide-react';
import Footer from '../../components/landing/Footer';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: CheckCircle,
      content: [
        "By accessing and using TripSecure services, you accept and agree to be bound by these terms",
        "If you do not agree to these terms, please do not use our services",
        "We reserve the right to modify these terms at any time",
        "Continued use of our services after changes constitutes acceptance of new terms",
        "You are responsible for reviewing these terms periodically"
      ]
    },
    {
      title: "Service Description",
      icon: Shield,
      content: [
        "TripSecure provides travel insurance, safety services, and logistics solutions",
        "Our services include Pay-As-You-Go (PAYG) travel insurance coverage",
        "We offer airport pickup, roadside assistance, and safety helpline services",
        "Group tours, pilgrimages, and custom travel arrangements are available",
        "All services are subject to availability and terms of coverage"
      ]
    },
    {
      title: "User Responsibilities",
      icon: Users,
      content: [
        "Provide accurate and complete information when using our services",
        "Maintain the confidentiality of your account credentials",
        "Use our services only for lawful purposes",
        "Comply with all applicable laws and regulations",
        "Notify us immediately of any unauthorized use of your account"
      ]
    },
    {
      title: "Payment Terms",
      icon: FileText,
      content: [
        "Payment is required before service activation",
        "We accept mobile money, credit cards, and bank transfers",
        "All prices are subject to change without notice",
        "Refunds are subject to our refund policy",
        "Failed payments may result in service suspension"
      ]
    },
    {
      title: "Limitation of Liability",
      icon: AlertCircle,
      content: [
        "TripSecure's liability is limited to the extent permitted by law",
        "We are not liable for indirect, incidental, or consequential damages",
        "Our total liability shall not exceed the amount paid for the service",
        "We are not responsible for third-party actions or services",
        "Force majeure events may limit our obligations"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Back Button */}
      <div className="fixed top-24 left-4 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Back</span>
        </motion.button>
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-600/10 to-blue-600/10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-gray-300/20" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-blue-300/20" />
        
        <div className="relative z-10 px-4 mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 mb-6">
              <Scale className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Terms of Service</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl mb-6">
              Terms of Service
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gray-600 to-blue-600">
                Our Terms and Conditions
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              These terms and conditions govern your use of TripSecure services. Please read them carefully before using our platform.
            </p>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 border border-blue-200">
              <span className="text-sm font-medium text-blue-700">Last Updated: December 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 px-4 mx-auto max-w-6xl sm:px-6">
        <div className="space-y-16">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              
              <div className="space-y-4">
                {section.content.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Important Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Important Notice</h2>
              <p className="text-lg opacity-90">
                These terms constitute a legal agreement between you and TripSecure. By using our services, you acknowledge that you have read, understood, and agree to be bound by these terms.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Legal Agreement</h3>
                <p className="text-sm opacity-90">These terms form a binding contract</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Service Protection</h3>
                <p className="text-sm opacity-90">Terms protect both parties</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">User Rights</h3>
                <p className="text-sm opacity-90">Your rights and responsibilities</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About These Terms?</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about these terms of service, please contact our legal team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:legal@tripsecuregh.com"
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Contact Legal Team
              </a>
              <a
                href="mailto:info@tripsecuregh.com"
                className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                General Inquiries
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
